/**
 * 听书播放条（App 独占）：仅在当前章节可读（detailStatus==='ready'）且原生环境时挂载。
 * 播控走 useTtsStore；合成/后台/锁屏由原生 TtsEngine 插件承担。
 * 手动切到其它章节时自动停止，避免朗读陈旧内容。
 */
import { useEffect } from 'react';
import { useTtsStore } from '../state/tts-store';

const RATES = [0.75, 1, 1.25, 1.5, 2];

function Icon({ name }: { name: 'play' | 'pause' | 'stop' | 'prev' | 'next' }) {
  const p = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'play':
      return (
        <svg {...p} fill="currentColor" stroke="none">
          <path d="M7 5l12 7-12 7z" />
        </svg>
      );
    case 'pause':
      return (
        <svg {...p} fill="currentColor" stroke="none">
          <rect x="6" y="5" width="4" height="14" rx="1" />
          <rect x="14" y="5" width="4" height="14" rx="1" />
        </svg>
      );
    case 'stop':
      return (
        <svg {...p} fill="currentColor" stroke="none">
          <rect x="6" y="6" width="12" height="12" rx="2" />
        </svg>
      );
    case 'prev':
      return (
        <svg {...p}>
          <path d="M18 6l-8 6 8 6z" fill="currentColor" stroke="none" />
          <line x1="6" y1="5" x2="6" y2="19" />
        </svg>
      );
    case 'next':
      return (
        <svg {...p}>
          <path d="M6 6l8 6-8 6z" fill="currentColor" stroke="none" />
          <line x1="18" y1="5" x2="18" y2="19" />
        </svg>
      );
  }
}

export default function TtsPlayerBar(props: {
  chapterId: number;
  title: string;
  contentMd: string;
}) {
  const { chapterId, title, contentMd } = props;
  const status = useTtsStore((s) => s.status);
  const storeChapterId = useTtsStore((s) => s.chapterId);
  const cursor = useTtsStore((s) => s.cursor);
  const total = useTtsStore((s) => s.total);
  const rate = useTtsStore((s) => s.rate);
  const autoNext = useTtsStore((s) => s.autoNext);
  const error = useTtsStore((s) => s.error);

  // 手动切章（store 当前章 ≠ 打开章）→ 停止，避免继续朗读上一条内容
  useEffect(() => {
    const s = useTtsStore.getState();
    if (s.chapterId != null && s.chapterId !== chapterId && (s.status === 'playing' || s.status === 'paused')) {
      s.stop();
    }
  }, [chapterId]);

  const isCurrent = storeChapterId === chapterId;
  const playing = isCurrent && status === 'playing';
  const paused = isCurrent && status === 'paused';
  const loading = isCurrent && status === 'loading';

  const onMain = () => {
    const s = useTtsStore.getState();
    if (playing) {
      s.toggle(); // → 暂停
      return;
    }
    if (paused) {
      s.toggle(); // → 继续
      return;
    }
    // idle / ended / 换章：从头朗读当前打开的章节
    void s.start(chapterId, title, contentMd);
  };

  const progress = total > 0 ? Math.min(100, Math.round(((cursor + 1) / total) * 100)) : 0;

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2">
        {/* 主播放/暂停 */}
        <button
          type="button"
          onClick={onMain}
          aria-label={playing ? '暂停' : '播放'}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white active:bg-indigo-700"
        >
          {playing ? <Icon name="pause" /> : <Icon name="play" />}
        </button>
        {/* 停止 */}
        <button
          type="button"
          onClick={() => useTtsStore.getState().stop()}
          aria-label="停止"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200"
        >
          <Icon name="stop" />
        </button>
        {/* 上一章 / 下一章 */}
        <button
          type="button"
          onClick={() => useTtsStore.getState().prevChapter()}
          aria-label="上一章"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200"
        >
          <Icon name="prev" />
        </button>
        <button
          type="button"
          onClick={() => useTtsStore.getState().nextChapter()}
          aria-label="下一章"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200"
        >
          <Icon name="next" />
        </button>
        <div className="min-w-0 flex-1" />
        {/* 倍速 */}
        <button
          type="button"
          onClick={() => {
            const i = RATES.indexOf(rate);
            useTtsStore.getState().setRate(RATES[(i + 1) % RATES.length]);
          }}
          className="shrink-0 rounded-full px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200"
        >
          {rate}×
        </button>
        {/* 自动下一章 */}
        <button
          type="button"
          onClick={() => useTtsStore.getState().setAutoNext(!autoNext)}
          className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${
            autoNext ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-200'
          }`}
        >
          连读
        </button>
      </div>

      {/* 进度 + 状态文案 */}
      <div className="mt-2.5">
        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
          <div className="h-full rounded-full bg-indigo-500 transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
          <span className="truncate pr-2">
            {loading ? '加载中…' : playing || paused ? `${title} · 第 ${cursor + 1}/${total} 句` : `听书：${title}`}
          </span>
          {status === 'ended' && isCurrent && !error && <span className="shrink-0">本章读完</span>}
        </div>
        {error && isCurrent && <div className="mt-1 text-xs text-red-500">{error}</div>}
      </div>
    </div>
  );
}
